/**
 * FileManagement - Handles file upload, download, and management for orders
 * Supports delivery notes, invoices, and additional files
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Alert,
  Grid,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  AttachFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { UnifiedOrder } from '../../types/unifiedOrderTypes';
import { unifiedOrderService } from '../../services/unifiedOrderService';
import { positionFileService } from '../../services/positionFileService';
import { useNotification } from '../../contexts/NotificationContext';
import { formatDate } from '../../utils/dateUtils';

interface FileManagementProps {
  order: UnifiedOrder;
  onFileUpdated: () => void;
}

interface FileInfo {
  type: 'pdf' | 'delivery_note' | 'invoice' | 'extra_file' | 'position_file';
  path: string;
  name: string;
  uploadDate?: string;
  positionNumber?: number;
  positionId?: string;
  fileId?: string; // Add fileId for position files
}

export default function FileManagement({ order, onFileUpdated }: FileManagementProps) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<'delivery_note' | 'invoice' | 'extra_file'>('delivery_note');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [positionFiles, setPositionFiles] = useState<any[]>([]);
  const [loadingPositionFiles, setLoadingPositionFiles] = useState(false);
  const { showNotification } = useNotification();

  // Load position files when component mounts
  useEffect(() => {
    loadPositionFiles();
  }, [order.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPositionFiles = async () => {
    try {
      setLoadingPositionFiles(true);
      console.log('🔍 [DEBUG] Loading position files for order:', order.id);
      
      const response = await positionFileService.getOrderPositionFiles(order.id);
      console.log('🔍 [DEBUG] Position files response:', response);
      
      if (response.success && response.data) {
        const allFiles = Object.values(response.data).flat();
        console.log('🔍 [DEBUG] Processed position files:', allFiles);
        setPositionFiles(allFiles);
      }
    } catch (error: any) {
      console.error('Error loading position files:', error);
      showNotification('Fehler beim Laden der Positionsdateien', 'error');
    } finally {
      setLoadingPositionFiles(false);
    }
  };

  // Collect all files from order
  const getOrderFiles = (): FileInfo[] => {
    const files: FileInfo[] = [];

    // PDF file
    if (order.pdf_path) {
      files.push({
        type: 'pdf',
        path: order.pdf_path,
        name: 'Bestell-PDF', /* Order PDF */
        uploadDate: order.created_at
      });
    }

    // Delivery note
    if (order.delivery_note_path) {
      files.push({
        type: 'delivery_note',
        path: order.delivery_note_path,
        name: 'Lieferschein', /* Delivery Note */
        uploadDate: order.updated_at
      });
    }

    // Invoice
    if (order.invoice_path) {
      files.push({
        type: 'invoice',
        path: order.invoice_path,
        name: 'Rechnung', /* Invoice */
        uploadDate: order.updated_at
      });
    }

    // Extra file
    if (order.extra_file_path) {
      files.push({
        type: 'extra_file',
        path: order.extra_file_path,
        name: 'Zusatzdatei', /* Extra File */
        uploadDate: order.updated_at
      });
    }

    // Add position files
    positionFiles.forEach((posFile) => {
      const position = order.positions.find(p => p.id === posFile.position_id);
      files.push({
        type: 'position_file',
        path: posFile.filename,
        name: posFile.originalname,
        uploadDate: posFile.uploaded_at,
        positionNumber: position?.position_number,
        positionId: posFile.position_id,
        fileId: posFile.id
      });
    });

    return files;
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      showNotification('Bitte wählen Sie eine Datei zum Hochladen aus', 'error'); /* Please select a file to upload */
      return;
    }

    try {
      setUploading(true);
      let response;

      switch (uploadType) {
        case 'delivery_note':
          response = await unifiedOrderService.uploadDeliveryNote(order.id, uploadFile);
          break;
        case 'invoice':
          response = await unifiedOrderService.uploadInvoice(order.id, uploadFile);
          break;
        case 'extra_file':
          response = await unifiedOrderService.uploadExtraFile(order.id, uploadFile);
          break;
        default:
          throw new Error('Invalid upload type');
      }

      if (response.success) {
        showNotification('Datei erfolgreich hochgeladen!', 'success'); /* File uploaded successfully! */
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadDescription('');
        onFileUpdated();
      } else {
        showNotification(response.error || 'Fehler beim Hochladen der Datei', 'error'); /* Failed to upload file */
      }
    } catch (error: any) {
      showNotification(`Fehler beim Hochladen der Datei: ${error.message}`, 'error'); /* Error uploading file: */
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (file: FileInfo) => {
    let url: string;
    
    if (file.type === 'position_file' && file.fileId && file.positionId) {
      url = unifiedOrderService.getPositionFileUrl(order.id, file.positionId, file.fileId);
    } else {
      url = unifiedOrderService.getFileUrl(file.path);
    }
    
    window.open(url, '_blank');
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <PdfIcon color="error" />;
      case 'delivery_note':
        return <DocumentIcon color="info" />;
      case 'invoice':
        return <DocumentIcon color="success" />;
      case 'extra_file':
        return <FileIcon color="primary" />;
      case 'position_file':
        return <DocumentIcon color="warning" />;
      default:
        return <FileIcon />;
    }
  };

  const getFileTypeChip = (type: string, positionNumber?: number) => {
    const typeMap = {
      pdf: { label: 'PDF', color: 'error' as const },
      delivery_note: { label: 'Lieferschein', color: 'info' as const }, /* Delivery Note */
      invoice: { label: 'Rechnung', color: 'success' as const }, /* Invoice */
      extra_file: { label: 'Zusatzdatei', color: 'primary' as const }, /* Extra File */
      position_file: { label: `Position ${positionNumber || '?'}`, color: 'warning' as const }
    };

    const config = typeMap[type as keyof typeof typeMap] || { label: 'Unbekannt', color: 'default' as const }; /* Unknown */
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const files = getOrderFiles();

  return (
    <Box>
      {/* File Upload Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Dateiverwaltung {/* File Management */}
            </Typography>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Datei hochladen {/* Upload File */}
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Bestelldokumente wie Lieferscheine, Rechnungen und zusätzliche Dateien verwalten {/* Manage order documents including delivery notes, invoices, and additional files */}
          </Typography>
        </CardContent>
      </Card>

      {/* Files List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bestelldateien ({files.length}) {/* Order Files */}
          </Typography>
          
          {files.length === 0 ? (
            <Alert severity="info">
              Es wurden noch keine Dateien hochgeladen. Klicken Sie auf "Datei hochladen", um Dokumente hinzuzufügen. {/* No files have been uploaded yet. Click "Upload File" to add documents. */}
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Datei</TableCell> {/* File */}
                    <TableCell>Typ</TableCell> {/* Type */}
                    <TableCell>Upload-Datum</TableCell> {/* Upload Date */}
                    <TableCell align="center">Aktionen</TableCell> {/* Actions */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file, index) => (
                    <TableRow key={`${file.type}-${index}`}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getFileIcon(file.type)}
                          <Typography variant="body2" fontWeight="medium">
                            {file.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getFileTypeChip(file.type, file.positionNumber)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {file.uploadDate ? formatDate(new Date(file.uploadDate)) : 'Unbekannt'} {/* Unknown */}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Herunterladen" /* Download */>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(file)}
                            color="primary"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Datei hochladen</DialogTitle> {/* Upload File */}
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Dateityp</InputLabel> {/* File Type */}
                <Select
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value as any)}
                  label="Dateityp" /* File Type */
                >
                  <MenuItem value="delivery_note">Lieferschein</MenuItem> {/* Delivery Note */}
                  <MenuItem value="invoice">Rechnung</MenuItem> {/* Invoice */}
                  <MenuItem value="extra_file">Zusatzdatei</MenuItem> {/* Extra File */}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <input
                  id="file-upload"
                  type="file"
                  hidden
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                />
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  {uploadFile ? uploadFile.name : 'Klicken Sie, um eine Datei auszuwählen'} {/* Click to select a file */}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unterstützt PDF, DOC, DOCX, JPG, PNG, GIF {/* Supports PDF, DOC, DOCX, JPG, PNG, GIF */}
                </Typography>
              </Box>
            </Grid>

            {uploadType === 'extra_file' && (
              <Grid item xs={12}>
                <TextField
                  label="Dateibeschreibung" /* File Description */
                  fullWidth
                  multiline
                  rows={2}
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Optionale Beschreibung für diese Datei..." /* Optional description for this file... */
                />
              </Grid>
            )}
          </Grid>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Datei wird hochgeladen... {/* Uploading file... */}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Abbrechen {/* Cancel */}
          </Button>
          <Button
            onClick={handleFileUpload}
            variant="contained"
            disabled={!uploadFile || uploading}
          >
            {uploading ? 'Wird hochgeladen...' : 'Datei hochladen'} {/* Uploading... : Upload File */}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
